//@ts-check
import React, { useMemo, useState } from 'react';
import { find, camelCase, isNil } from 'lodash-es';
import { produce } from 'immer';

import { IPdpData, IProduct, TUserSelection, TUserSelections } from '../types/PDP';
import { WindowServerPdpData } from '../types/ServerPDPData';
import Product from './Product';
import { lowerCasedBlacklistedOptionValues } from '../../../utils/constants';
import Fuse from 'fuse.js';

const urlSearchParams = new URLSearchParams(window.location.search);
const variantsParams = urlSearchParams.get('variants') || '';
const variantIds = variantsParams.split(',');

const variantId = urlSearchParams.get('variant');
const hasExactSelection = !!variantId || !!(variantIds && variantIds.length);

const PDP = ({ pdpData }: { pdpData: IPdpData }) => {
  const [userSelections, setUserSelections] = useState(() => {
    const { isASet, products } = pdpData;
    let initialSelections: TUserSelection[] = isASet
      ? computeSetsInitialSelections(products)
      : // ? [REVISIT] make sure this `!` is safe
        [computeSingleProductInitialSelections(products[0]!)];

    if (!hasExactSelection) {
      initialSelections = initialSelections.map((selection) => {
        return { ...selection, Size: undefined };
      });
    }

    // defaults userSelections w/ only 1 option OR if all values are blacklisted ex: 'One Size'
    products.forEach((product, index) => {
      Object.entries(product.options).forEach((entry) => {
        //@ts-ignore, being generic here w/o hard-coding 'Color' & 'Size'
        const [name, values]: ['Color' | 'Size', string[]] = entry;

        const allValuesInBlackList = values.every((item) =>
          lowerCasedBlacklistedOptionValues.includes(item.toLowerCase().replace(' ', ''))
        );

        const currentSelection = initialSelections[index];
        if ((values.length === 1 || allValuesInBlackList) && currentSelection && !currentSelection[name]) {
          currentSelection[name] = values[0];
        }
      });
    });

    return initialSelections;
  });

  const { isASet } = pdpData;

  // Sets color sync
  let displayUserSelections: TUserSelection[] = useMemo(() => {
    return isASet && userSelections[0]
      ? userSelections.map((selection: TUserSelection, index) => {
          const selectionClone = { ...selection };
          const currentProduct = pdpData.products[index];
          if (index === 0 || !currentProduct) return selectionClone;

          const primaryProductColor = userSelections[0]?.Color ?? '';
          if (isNil(selectionClone.Color)) {
            const colorOptionValues = currentProduct.options.Color;

            const colorsIndex = new Fuse(
              colorOptionValues,
              // ? [REVISIT] options https://fusejs.io/api/options.html
              {
                includeScore: true,
                // findAllMatches: true,
                // includeMatches: true,
                ignoreLocation: true,
                useExtendedSearch: true,
                // threshold: 0,
              }
            );
            const fuzzyColorMatch = colorsIndex.search(primaryProductColor.split(' ').join('|'))?.[0]?.item;
            // ? [REVISIT] likely un-necessary with the 'fuzzy match'
            const exactColorMatch = colorOptionValues?.find((color) => color === primaryProductColor);
            const matchingColor = exactColorMatch ?? fuzzyColorMatch;

            if (matchingColor) {
              selectionClone.Color = matchingColor;
            } else {
              // ? [REVISIT] we could also opt not to swap away from what are currently on & just let it be
              selectionClone.Color = currentProduct.options.Color[0];
            }
          }
          return selectionClone;
        })
      : userSelections;
  }, [isASet, pdpData.products, userSelections]);

  // * [IMPROVEMENT] 1) better performance w/ useCallback(); 2) currying this function arguably leads to cleaner code; 3) possible pitfall hard-coding 'color' & 'size
  const updateUserSelection = (productIndex: number, optionName: 'Color' | 'Size', newValue: string) => {
    setUserSelections(
      produce((selections: TUserSelection[]) => {
        // ? [REVISIT] make sure this `!` is safe; how do we express this in TS
        const selection = selections[productIndex]!;
        selection[optionName] = newValue;

        // De-sync Set's color sync if size is selected & we can inherit color
        if (isASet && optionName === 'Size' && productIndex > 0 && !selection.Color) {
          // ? [REVISIT] make sure this `!` is safe; how do we express this in TS
          const inheritableColor = displayUserSelections[productIndex]!.Color;
          selection.Color = inheritableColor;
        }
      })
    );
  };

  //@ts-expect-error
  const nonEmptyUserSelections: TUserSelections = displayUserSelections;

  return <Product pdpData={pdpData} userSelections={nonEmptyUserSelections} updateSelection={updateUserSelection} />;
};

export default PDP;

// Helper functions
function computeSetsInitialSelections(products: IProduct[]) {
  const urlSearchParams = new URLSearchParams(window.location.search);

  const color = urlSearchParams.get('color');
  const getMatchingProductColor = (color: string | null, product: IProduct) =>
    color && product.options.Color.find((colorOption) => stringContentEquality(colorOption, color));
  // ? [REVISIT] make sure this `!` is safe; how do we express this in TS
  const matchingPrimaryProductColor = getMatchingProductColor(color, products[0]!);
  return products.map((product, index) => {
    if (variantIds && variantIds.length) {
      const { variants } = product;
      const variantIdMatch = variantIds[index] && find(variants, { id: variantIds[index] });
      if (variantIdMatch) return variantIdMatch.selectionOptions;
    }

    // const matchingProductColor = getMatchingProductColor(color, product);

    // ? [REVISIT] go over w/ business the various alternatives for doing this, alternatives below
    // options for setting initial color
    // const colorMatchForAllProductsWithSyncWhenWherePossible =
    //   index === 0
    //     ? matchingPrimaryProductColor || product.options.Color[0]
    //     : matchingPrimaryProductColor && matchingProductColor
    //     ? undefined
    //     : matchingProductColor;
    // const colorMatchForAllProductsNoSync = matchingProductColor || (index === 0 ? product.options.Color[0] : undefined);
    const colorMatchFor1stProductOnly =
      index === 0 ? matchingPrimaryProductColor || product.options.Color[0] : undefined; // Inherently preserves sync
    return {
      Color: colorMatchFor1stProductOnly,
      Size: undefined,
    };
  });
}

function computeSingleProductInitialSelections(product: IProduct) {
  const { variants } = product;

  // 'variantId' query param
  const variantIdMatch = variantId && find(variants, { id: variantId });
  if (variantIdMatch) return variantIdMatch.selectionOptions;

  // 'color' query param
  const color = urlSearchParams.get('color');
  const colorMatch =
    color &&
    variants.find(
      (variant) => variant.availableForSale && stringContentEquality(variant.selectionOptions.Color, color)
    );
  if (colorMatch) return colorMatch.selectionOptions;

  // ? [REVISIT] assumes too much about option1 (1st === Color) & doesn't deal w/ blacklisted values
  //@ts-ignore
  const serverPdpData: WindowServerPdpData = window.server_pdp_data;

  if (serverPdpData.product.variants[0]?.options)
    return { Color: serverPdpData.product.variants[0].option1, Size: undefined };
  // first available
  const firstSellable = find(variants, { availableForSale: true });
  if (firstSellable) return firstSellable.selectionOptions;

  // come on ... really now ... :); should never get here --> 1st variant
  // ? [REVISIT] make sure this `!` is safe; can we assume atleast 1 variant?
  return variants[0]!.selectionOptions;
}

// not meant to a generic solution, should not be exported
const stringContentEquality = (first: string, second: string) => {
  const [one, two] = [first, second].map((item) => camelCase(item).toLowerCase());
  return one === two;
};
